/**
 * Search, assignment, lifecycle, and ordering controls for the notes inbox.
 *
 * The parent page owns state and translates these controls into server queries.
 */

import { Search } from "lucide-react";
import type { NoteAssigneeResponse } from "../../api/clinicalTrialsApiTypes";
import type { NotesFilters } from "../../types/notes";

interface NotesFiltersBarProps {
  filters: NotesFilters;
  assignees: NoteAssigneeResponse[];
  onChange: (filters: NotesFilters) => void;
}

/**
 * Render server-backed notes filter controls.
 *
 * @param props - Current filters, assignment directory, and update callback
 * @returns Notes inbox toolbar
 */
export function NotesFiltersBar({
  filters,
  assignees,
  onChange,
}: NotesFiltersBarProps) {
  const toggle = (key: "unreadOnly" | "assignedToMe" | "overdueOnly") =>
    onChange({ ...filters, [key]: !filters[key] });
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <label className="flex min-w-[280px] flex-1 items-center gap-2 rounded-md border border-uiGray-300 bg-white px-3 py-2">
        <Search size={15} className="text-primaryGray-9" />
        <input
          type="search"
          aria-label="Search notes"
          placeholder="Search notes, patients, MRNs, creators, or assignees"
          value={filters.query}
          onChange={(event) => onChange({ ...filters, query: event.target.value })}
          className="w-full bg-transparent text-sm outline-none"
        />
      </label>
      {[
        ["unreadOnly", "Unread"],
        ["assignedToMe", "Assigned to me"],
        ["overdueOnly", "Overdue"],
      ].map(([key, label]) => (
        <button
          key={key}
          type="button"
          aria-pressed={filters[key as keyof NotesFilters] === true}
          onClick={() =>
            toggle(key as "unreadOnly" | "assignedToMe" | "overdueOnly")
          }
          className={`rounded-md border px-3 py-2 text-xs font-medium ${
            filters[key as keyof NotesFilters]
              ? "border-statusBlue-text bg-statusBlue-bg text-statusBlue-darkText"
              : "border-uiGray-300 bg-white text-primaryGray-7"
          }`}
        >
          {label}
        </button>
      ))}
      <select
        aria-label="Filter by assignee"
        value={filters.assigneeId}
        onChange={(event) =>
          onChange({ ...filters, assigneeId: event.target.value })
        }
        className="rounded-md border border-uiGray-300 bg-white px-3 py-2 text-xs"
      >
        <option value="">All assignees</option>
        {assignees.map((assignee) => (
          <option key={assignee.user_id} value={assignee.user_id}>
            {assignee.display_name || assignee.email}
          </option>
        ))}
      </select>
      <select
        aria-label="Filter by status"
        value={filters.status}
        onChange={(event) =>
          onChange({
            ...filters,
            status: event.target.value as NotesFilters["status"],
          })
        }
        className="rounded-md border border-uiGray-300 bg-white px-3 py-2 text-xs"
      >
        <option value="all">All statuses</option>
        <option value="open">Open</option>
        <option value="resolved">Resolved</option>
      </select>
      <select
        aria-label="Sort notes"
        value={`${filters.orderBy}:${filters.orderDirection}`}
        onChange={(event) => {
          const [orderBy, orderDirection] = event.target.value.split(":");
          onChange({
            ...filters,
            orderBy: orderBy as NotesFilters["orderBy"],
            orderDirection: orderDirection as NotesFilters["orderDirection"],
          });
        }}
        className="rounded-md border border-uiGray-300 bg-white px-3 py-2 text-xs"
      >
        <option value="created_at:desc">Newest created</option>
        <option value="updated_at:desc">Latest activity</option>
        <option value="due_date:asc">Due date</option>
      </select>
    </div>
  );
}
